import { FC } from 'react';
import { useContextualTour } from '@/hooks/useContextualTour';
import { useIsMobile } from '@/hooks/useIsMobile';
import { TUTORIAL_KEYS } from '@/constants/tutorials';
import DesktopTour from './DesktopTour';
import MobileWelcomeModal from './MobileWelcomeModal';
import type { Config } from 'driver.js';

/**
 * Tour contextual de Cursos — dispara na 1ª visita a `/trilhas`.
 * Desktop: 3 passos com spotlights. Mobile: modal único de boas-vindas.
 */
const DESKTOP_STEPS: NonNullable<Config['steps']> = [
  {
    popover: {
      title: 'Aqui ficam todos os cursos',
      description:
        'Trilhas são conjuntos de aulas curtas sobre um tema. Você pode fazer no seu ritmo — começa, para, volta.',
    },
  },
  {
    element: '[data-tour="trails-search"]',
    popover: {
      title: 'Filtre por tema ou busque pelo nome',
      description:
        'Use os chips abaixo pra filtrar por área (Automação, Governança, IA) ou a busca pra achar pelo nome. Pra começar do zero, recomendamos a <b>Trilha 1</b>.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour="trails-card"]',
    popover: {
      title: 'Cada card é um curso',
      description:
        'Aqui você vê o nível, a duração, o XP que vai ganhar e quanto já progrediu. Clica em <b>Continuar</b> pra começar ou seguir de onde parou.',
      side: 'top',
    },
  },
];

const TrailsTour: FC = () => {
  const { shouldStart, finishTour, skipTour } = useContextualTour(TUTORIAL_KEYS.TRAILS, '/trilhas');
  const isMobile = useIsMobile();

  if (!shouldStart) return null;

  return isMobile ? (
    <MobileWelcomeModal
      onComplete={finishTour}
      onSkip={skipTour}
      content={{
        emoji: '📚',
        title: 'Bem-vindo às Trilhas',
        subtitle: 'Aqui ficam todos os cursos do h.lab.',
        tipTitle: 'Como começar:',
        tipBody: (
          <>
            Use os <b className="text-accent">filtros</b> ou a <b className="text-accent">busca</b>{' '}
            pra achar um curso. Recomendamos começar pela{' '}
            <b className="text-content-primary">Trilha 1 — Fundamentos de IA</b>.
          </>
        ),
        ctaLabel: 'Explorar trilhas',
      }}
    />
  ) : (
    <DesktopTour steps={DESKTOP_STEPS} onComplete={finishTour} onSkip={skipTour} />
  );
};

export default TrailsTour;
